import type { RouteLocationNormalized } from 'vue-router'
import { confirmPageReload } from '~/utils/confirmPageReload'

const NAVIGATION_RELOAD_KEY = 'advisor:navigation-reload'
const NAVIGATION_RELOAD_TTL = 10_000

export default defineNuxtPlugin((nuxtApp) => {
  const route = useRoute()
  const router = useRouter()
  let recovering = false
  let lastPromptAt = -Infinity
  let navigating = false
  let navigationChunkError: unknown
  const seenErrors = new WeakSet<object>()
  const isModuleLoadError = (error: unknown) => error instanceof Error
    && /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i.test(error.message)
  const markSeen = (error: unknown) => {
    if (error && typeof error === 'object')
      seenErrors.add(error)
  }
  // Reloading loses component inputs and Pinia drafts. Let the user keep the
  // current page; manual Nuxt recovery prevents a router reload bypassing this.
  async function recover(error: unknown) {
    // A single import rejection can reach Vite, Vue and the browser. Leave
    // these errors observable, but ask only once and limit repeated failures.
    if (error && typeof error === 'object') {
      if (seenErrors.has(error))
        return
      // During navigation the router decides; see reloadInto.
      if (navigating) {
        navigationChunkError ??= error
        return
      }
      seenErrors.add(error)
    }
    if (recovering || Date.now() - lastPromptAt < 30_000)
      return
    recovering = true
    lastPromptAt = Date.now()
    try {
      const t = nuxtApp.$i18n.t
      if (await confirmPageReload(t('reload_after_load_failure'), t('reload_page'), t('cancel')))
        nuxtApp.runWithContext(() => reloadNuxtApp({ path: route.fullPath, persistState: true, ttl: 60_000 }))
    }
    finally {
      recovering = false
    }
  }

  // A tab that outlives a deployment requests chunks the new build removed.
  // Leave guards have already passed and the user chose the destination, so
  // load it from the current build instead of asking. If that same load fails
  // again, fall back to the prompt rather than looping or doing nothing.
  function reloadInto(to: RouteLocationNormalized, error: unknown) {
    markSeen(error)
    let recent: { path?: string, expires?: number } = {}
    try {
      recent = JSON.parse(sessionStorage.getItem(NAVIGATION_RELOAD_KEY) || '{}')
    }
    catch {}
    if (recent.path === to.fullPath && (recent.expires ?? 0) > Date.now()) {
      seenErrors.delete(error as object)
      return recover(error)
    }
    try {
      sessionStorage.setItem(NAVIGATION_RELOAD_KEY, JSON.stringify({ path: to.fullPath, expires: Date.now() + NAVIGATION_RELOAD_TTL }))
    }
    catch {}
    nuxtApp.runWithContext(() => reloadNuxtApp({ path: to.fullPath, persistState: true, ttl: NAVIGATION_RELOAD_TTL }))
  }

  router.beforeEach(() => {
    navigating = true
    navigationChunkError = undefined
  })
  // Nuxt suppresses CSS preload failures, so such a navigation still completes.
  router.afterEach((to, _from, failure) => {
    navigating = false
    if (navigationChunkError && !failure)
      void reloadInto(to, navigationChunkError)
  })
  router.onError((error, to) => {
    navigating = false
    if (isModuleLoadError(error) || navigationChunkError)
      void reloadInto(to, error)
  })

  nuxtApp.hook('app:chunkError', ({ error }) => recover(error))
  nuxtApp.hook('vue:error', (error) => {
    if (isModuleLoadError(error))
      return recover(error)
  })
  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    if (isModuleLoadError(event.reason))
      void recover(event.reason)
  }
  window.addEventListener('unhandledrejection', onUnhandledRejection)
  if (import.meta.hot) {
    import.meta.hot.dispose(() => window.removeEventListener('unhandledrejection', onUnhandledRejection))
  }
})
